import { useEffect } from 'react';

interface MetaTagConfig {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

const PAGE_META_DATA: Record<string, MetaTagConfig> = {
  '/': {
    title: 'Best Real Estate Company in Hyderabad | Home and Own',
    description: 'Best real estate company in Hyderabad — Home and Own offers expert guidance for buying, selling, renting, and managing properties—find your dream home!',
    keywords: 'best real estate company in hyderabad, property in hyderabad, homes for sale hyderabad, rent property hyderabad',
    canonical: 'https://homeandown.com/',
  },
  '/buy': {
    title: 'Buy Property in Hyderabad — Verified Listings | Home and Own',
    description: 'Looking to buy property in Hyderabad? Visit Home and Own and explore verified listings, expert support, and the best homes in prime locations across the city.',
    keywords: 'buy property in hyderabad, homes for sale hyderabad, property for purchase hyderabad',
    canonical: 'https://homeandown.com/buy',
  },
  '/rent': {
    title: 'Property for Rent in Hyderabad — Flats & Homes | Home and Own',
    description: 'Find the perfect property for rent in Hyderabad at Home and Own. Includes verified listings, flexible options, and expert assistance for hassle-free renting.',
    keywords: 'property for rent in hyderabad, apartments for rent hyderabad, flats for rent hyderabad',
    canonical: 'https://homeandown.com/rent',
  },
  '/sell': {
    title: 'Sell Property in Hyderabad — Get Best Value | Home and Own',
    description: 'List your property for sale in Hyderabad at Home and Own, with expert pricing, marketing support, and serious buyers to ensure a fast, profitable deal.',
    keywords: 'property for sale in hyderabad, sell property hyderabad, home selling hyderabad',
    canonical: 'https://homeandown.com/sell',
  },
  '/property-management': {
    title: 'Property Management Companies in Hyderabad | Home and Own',
    description: 'Choose Home and Own, one of the leading property management companies in Hyderabad, for tenant handling, maintenance, and complete property care services.',
    keywords: 'property management companies in hyderabad, property management services hyderabad',
    canonical: 'https://homeandown.com/property-management',
  },
  '/agent': {
    title: 'Real Estate Agents in Hyderabad — Trusted Pros | Home and Own',
    description: 'Find trusted real estate agents in Hyderabad at Home and Own for buying, selling, or renting properties with expert guidance and personalized support.',
    keywords: 'real estate agents in hyderabad, property agents hyderabad, real estate professionals',
    canonical: 'https://homeandown.com/agent',
  },
  '/contact-us': {
    title: 'Real Estate Companies in Hyderabad | Contact Home and Own',
    description: 'Contact Home and Own, one of the trusted real estate companies in Hyderabad, for expert guidance with buying, selling, or renting property across India.',
    keywords: 'real estate companies in hyderabad, property companies hyderabad, real estate services',
    canonical: 'https://homeandown.com/contact-us',
  },
  // No meta tags for /host – intentionally empty so this route does not get custom title, description, canonical, or og/twitter
  '/host': {},
};

/**
 * Hook to update meta tags based on the current route
 * Usage: useMetaTags('/buy') in your page component
 */
export function useMetaTags(pathname: string, customMeta?: MetaTagConfig) {
  useEffect(() => {
    const meta = customMeta || PAGE_META_DATA[pathname] || PAGE_META_DATA['/'];

    // Update title
    if (meta.title) {
      document.title = meta.title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      let element = document.querySelector(property ? `meta[property="${name}"]` : `meta[name="${name}"]`) as HTMLMetaElement | null;

      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }

      element.content = content;
    };

    // Update description
    if (meta.description) {
      updateMetaTag('description', meta.description);
    }

    // Update keywords
    if (meta.keywords) {
      updateMetaTag('keywords', meta.keywords);
    }

    // Update canonical
    if (meta.canonical) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = meta.canonical;
    }

    // Update Open Graph tags
    if (meta.ogTitle) {
      updateMetaTag('og:title', meta.ogTitle, true);
    }
    if (meta.ogDescription) {
      updateMetaTag('og:description', meta.ogDescription, true);
    }
    if (meta.ogUrl) {
      updateMetaTag('og:url', meta.ogUrl, true);
    }
    if (meta.ogImage) {
      updateMetaTag('og:image', meta.ogImage, true);
    }

    // Update Twitter Card tags
    if (meta.twitterTitle) {
      updateMetaTag('twitter:title', meta.twitterTitle);
    }
    if (meta.twitterDescription) {
      updateMetaTag('twitter:description', meta.twitterDescription);
    }
    if (meta.twitterImage) {
      updateMetaTag('twitter:image', meta.twitterImage);
    }
  }, [pathname, customMeta]);
}

/**
 * Alternative: Use this selector to automatically update meta tags
 * Call this in your main App.tsx with the current location
 */
export function useAutoMetaTags() {
  const pathname = window.location.pathname;
  useMetaTags(pathname);
}
